/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from "react";

import { ArrowLeftIcon, ArrowRightIcon } from "@/assets/icons";
import { ChartTooltip } from "@/components/ui/chart/ChartTooltip";
import ToolTipBase from "@/components/ui/tooltip";

import {
  Bar,
  BarChart,
  ResponsiveContainer,
  ResponsiveContainerProps,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { twMerge } from "tailwind-merge";

import { domainTickCalculate } from "./utils";

const MAX_VISIBLE_BARS = 10;

const XAxisTick = (props: any) => {
  const { x, y, payload } = props;
  const maxLength = 14;
  const fullLabel = payload.value;

  const displayLabel =
    fullLabel.length > maxLength
      ? `${fullLabel.slice(0, maxLength)}...`
      : fullLabel;

  return (
    <g transform={`translate(${x},${y})`}>
      {fullLabel.length > maxLength ? (
        <ToolTipBase content={fullLabel} position="top-center">
          <text
            x={-15}
            y={10}
            dy={10}
            textAnchor="middle"
            fill="#000000B2"
            transform="rotate(-45)"
            fontSize={12}
            style={{
              cursor: "pointer"
            }}
          >
            {displayLabel}
          </text>
        </ToolTipBase>
      ) : (
        <text
          x={-15}
          y={10}
          dy={10}
          textAnchor="middle"
          fill="#000000B2"
          transform="rotate(-45)"
          fontSize={12}
          style={{
            cursor: "default"
          }}
        >
          {displayLabel}
        </text>
      )}
    </g>
  );
};

interface VerticalBarChartProps
  extends Omit<ResponsiveContainerProps, "children"> {
  data: {
    name: string;
    key: number;
  }[];
  title?: string;
  onBarClick?: (name: string) => void;
}

const VerticalBarChart = React.forwardRef<
  HTMLDivElement,
  VerticalBarChartProps
>((props, ref) => {
  const { data, title, onBarClick, ...rest } = props;

  const [currentPage, setCurrentPage] = useState(0);

  // Transform data to have a consistent "value" key for Recharts
  // Extract the first non-"name" property dynamically
  const chartData = data.map((item) => {
    const dataItem = item as Record<string, string | number>;
    const key = Object.keys(dataItem)[1];
    const valueKey = Object.keys(dataItem).find((k) => k !== "name");
    const value = valueKey ? Number(dataItem[valueKey]) : Number(item.key);

    return {
      name: item.name,
      key: key,
      value: value || 0
    };
  });

  // Calculate pagination
  const totalPages = Math.ceil(chartData.length / MAX_VISIBLE_BARS);
  const startIndex = currentPage * MAX_VISIBLE_BARS;
  const endIndex = startIndex + MAX_VISIBLE_BARS;
  const visibleData = chartData.slice(startIndex, endIndex);
  const showNavigation = chartData.length > MAX_VISIBLE_BARS;

  // Reset to first page when data changes
  React.useEffect(() => {
    setCurrentPage(0);
  }, [data.length]);

  const numericValues = chartData
    .map((item) => item.value)
    .filter((v) => !isNaN(v));
  const maxValue = numericValues.length > 0 ? Math.max(...numericValues, 0) : 0;

  const { axisDomainMax, axisTicks } = React.useMemo(
    () => domainTickCalculate(numericValues, maxValue),
    [numericValues, maxValue]
  );

  const totalValue = numericValues.reduce((sum, val) => sum + val, 0);

  const handlePrevious = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  };

  return (
    <div ref={ref} className="w-full relative flex items-center">
      {showNavigation && (
        <button
          onClick={handlePrevious}
          disabled={currentPage === 0}
          aria-label="Previous page"
          className={twMerge(
            "flex items-center justify-center w-8 h-8 rounded-full transition-colors flex-shrink-0 mr-2",
            currentPage === 0
              ? "text-gray-300 cursor-not-allowed"
              : "text-gray-700 hover:bg-gray-100 active:bg-gray-200"
          )}
        >
          <ArrowLeftIcon className="w-8 h-8" />
        </button>
      )}

      <div className="flex-1">
        <ResponsiveContainer {...rest} width="100%" height={rest.height || 400}>
          <BarChart
            data={visibleData}
            margin={{ top: 20, right: 20, left: 0, bottom: -15 }}
            aria-label={title}
          >
            <XAxis
              dataKey="name"
              type="category"
              height={80}
              axisLine={false}
              tickLine={false}
              interval={0}
              tick={<XAxisTick />}
            />
            <YAxis
              type="number"
              domain={[0, axisDomainMax * 1.2]}
              ticks={axisTicks}
              allowDecimals={false}
              tickLine={false}
              axisLine={false}
              tick={false}
              width={0}
            />
            <Tooltip
              content={(content) => (
                <ChartTooltip
                  {...content}
                  totalValue={totalValue}
                  getLabelFromItem={(item) => item.payload.key}
                  formatPercentage={(percent) => percent.toFixed(1)}
                />
              )}
            />
            <Bar
              dataKey="value"
              fill="#088C7A"
              className="font-semibold text-[#000000]/70"
              maxBarSize={40}
              radius={[999, 999, 0, 0]}
              onClick={(data) => onBarClick?.(data.name)}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {showNavigation && (
        <button
          onClick={handleNext}
          disabled={currentPage === totalPages - 1}
          aria-label="Next page"
          className={twMerge(
            "flex items-center justify-center w-8 h-8 rounded-full transition-colors flex-shrink-0 ml-2 fill-dim-gray",
            currentPage === totalPages - 1
              ? "text-gray-300 cursor-not-allowed"
              : "text-gray-700 hover:bg-gray-100 active:bg-gray-200"
          )}
        >
          <ArrowRightIcon className="w-8 h-8" />
        </button>
      )}
    </div>
  );
});

VerticalBarChart.displayName = "VerticalBarChart";

export default VerticalBarChart;
